/**
 * Scroll helpers + batched ScrollTrigger.refresh for native window scroll.
 * Refreshes are deferred until the user stops scrolling.
 * Dispatches SOJ_SCROLL_READY_EVENT and legacy "sojLenisReady".
 */

export const SOJ_SCROLL_READY_EVENT = "sojScrollReady";

/** @deprecated Same as SOJ_SCROLL_READY_EVENT — kept for old imports */
export const SOJ_LENIS_READY_EVENT = SOJ_SCROLL_READY_EVENT;

const REFRESH_DEBOUNCE_MS = 160;
const SCROLL_IDLE_MS = 250;
const HASH_FOLLOW_MS = 3500;
const DEFAULT_ANCHOR_OFFSET = -100;

class SmoothScroll {
  constructor() {
    this.isInitialized = false;
    /** @type {number | null} */
    this._layoutRefreshTimer = null;
    /** @type {boolean} */
    this._postMountImgListenersBound = false;
    /** @type {number} */
    this._lastScrollAt = 0;
    /** @type {boolean} */
    this._touchActive = false;
    /** @type {(() => void) | null} */
    this._onScrollActivity = null;
    /** @type {(() => void) | null} */
    this._onTouchStart = null;
    /** @type {(() => void) | null} */
    this._onTouchEnd = null;
    /** @type {boolean} */
    this._userReleasedHash = true;
    /** @type {number} */
    this._hashFollowUntil = 0;
    /** @type {((event: Event) => void) | null} */
    this._onHashChange = null;
    /** @type {((event: MouseEvent) => void) | null} */
    this._onHashClick = null;
    /** @type {(() => void) | null} */
    this._releaseHashFollow = null;
    this.init();
  }

  init() {
    try {
      this.isInitialized = true;
      this._bindScrollActivity();

      queueMicrotask(() => {
        const detail = { smoothScroll: this };
        document.dispatchEvent(
          new CustomEvent(SOJ_SCROLL_READY_EVENT, {
            bubbles: true,
            detail,
          }),
        );
        document.dispatchEvent(
          new CustomEvent("sojLenisReady", {
            bubbles: true,
            detail,
          }),
        );
      });

      queueMicrotask(() => {
        this._attachPostMountImageListeners();
        if (document.readyState !== "complete") {
          window.addEventListener(
            "load",
            () => this.scheduleRefreshDimensions(),
            { once: true },
          );
        }
        this._bindHashScrolling();
        this._handleInitialHash();
      });
    } catch {
      //
    }
  }

  _bindScrollActivity() {
    this._onScrollActivity = () => {
      this._lastScrollAt = performance.now();
    };
    this._onTouchStart = () => {
      this._touchActive = true;
      this._lastScrollAt = performance.now();
    };
    this._onTouchEnd = () => {
      this._touchActive = false;
      this._lastScrollAt = performance.now();
    };
    const opts = { passive: true };
    window.addEventListener("scroll", this._onScrollActivity, opts);
    window.addEventListener("touchstart", this._onTouchStart, opts);
    window.addEventListener("touchend", this._onTouchEnd, opts);
    window.addEventListener("touchcancel", this._onTouchEnd, opts);
  }

  /**
   * ScrollTrigger.refresh() rewrites the scroll position, which kills iOS
   * momentum scrolling — only refresh once the page is at rest.
   */
  isScrolling() {
    return (
      this._touchActive ||
      performance.now() - this._lastScrollAt < SCROLL_IDLE_MS
    );
  }

  _getAnchorOffset() {
    const header = document.getElementById("masthead");
    if (!header) return DEFAULT_ANCHOR_OFFSET;
    return -Math.ceil(header.getBoundingClientRect().height);
  }

  _resolveHashTarget(hash = window.location.hash) {
    if (!hash || hash === "#") return null;
    const raw = hash.charAt(0) === "#" ? hash.slice(1) : hash;
    if (!raw) return null;
    try {
      const decoded = decodeURIComponent(raw);
      return document.getElementById(decoded) || document.getElementById(raw);
    } catch {
      return document.getElementById(raw);
    }
  }

  _scrollToHash(options = {}) {
    const el = this._resolveHashTarget(options.hash ?? window.location.hash);
    if (!el) return false;
    this.scrollToNative(el, {
      immediate: options.immediate !== false,
      offset: this._getAnchorOffset(),
      duration: options.duration,
    });
    return true;
  }

  _maybeFollowHash() {
    if (this._userReleasedHash || !window.location.hash) return;
    if (performance.now() > this._hashFollowUntil) {
      this._userReleasedHash = true;
      return;
    }
    this._scrollToHash({ immediate: true });
  }

  _bindHashScrolling() {
    this._releaseHashFollow = () => {
      this._userReleasedHash = true;
    };

    this._onHashChange = () => {
      this._userReleasedHash = false;
      this._hashFollowUntil = performance.now() + HASH_FOLLOW_MS;
      this._scrollToHash({ immediate: false, duration: 1 });
    };
    window.addEventListener("hashchange", this._onHashChange);

    this._onHashClick = (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const link = event.target instanceof Element
        ? event.target.closest("a[href]")
        : null;
      if (!link) return;

      let url;
      try {
        url = new URL(link.href, window.location.href);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) return;
      if (url.pathname !== window.location.pathname) return;
      if (url.search !== window.location.search) return;
      if (!url.hash || url.hash === "#") return;
      if (!this._resolveHashTarget(url.hash)) return;

      event.preventDefault();
      this._userReleasedHash = false;
      this._hashFollowUntil = performance.now() + HASH_FOLLOW_MS;
      if (url.hash !== window.location.hash) {
        history.pushState(null, "", url.hash);
      }
      this._scrollToHash({ hash: url.hash, immediate: false, duration: 1 });
    };
    document.addEventListener("click", this._onHashClick);
  }

  _handleInitialHash() {
    if (!this._resolveHashTarget()) return;

    this._userReleasedHash = false;
    this._hashFollowUntil = performance.now() + HASH_FOLLOW_MS;
    this._scrollToHash({ immediate: true });
    requestAnimationFrame(() => {
      this._maybeFollowHash();
      requestAnimationFrame(() => this._maybeFollowHash());
    });

    const onLoad = () => {
      this._maybeFollowHash();
      window.setTimeout(() => this._maybeFollowHash(), 250);
      window.setTimeout(() => this._maybeFollowHash(), 800);
    };

    if (document.readyState === "complete") {
      onLoad();
    } else {
      window.addEventListener("load", onLoad, { once: true });
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this._maybeFollowHash()).catch(() => {});
    }

    window.setTimeout(() => {
      window.addEventListener("wheel", this._releaseHashFollow, {
        passive: true,
        once: true,
      });
      window.addEventListener("touchmove", this._releaseHashFollow, {
        passive: true,
        once: true,
      });
    }, 800);
  }

  _attachPostMountImageListeners() {
    if (this._postMountImgListenersBound) return;
    this._postMountImgListenersBound = true;

    const bump = () => this.scheduleRefreshDimensions();
    const list = document.getElementsByTagName("img");
    for (let i = 0; i < list.length; i++) {
      const el = list[i];
      if (el.complete) continue;
      el.addEventListener("load", bump, { passive: true, once: true });
      el.addEventListener("error", bump, { passive: true, once: true });
    }
  }

  scheduleRefreshDimensions() {
    if (this._layoutRefreshTimer !== null) {
      window.clearTimeout(this._layoutRefreshTimer);
    }
    this._layoutRefreshTimer = window.setTimeout(() => {
      this._layoutRefreshTimer = null;
      if (this.isScrolling()) {
        this.scheduleRefreshDimensions();
        return;
      }
      this.refreshDimensions();
      this._maybeFollowHash();
    }, REFRESH_DEBOUNCE_MS);
  }

  refreshDimensions() {
    if (typeof window.ScrollTrigger !== "undefined") {
      window.ScrollTrigger.refresh();
    }
  }

  scrollToNative(target, options = {}) {
    const immediate = !!options.immediate;
    const offset =
      typeof options.offset === "number" ? options.offset : 0;
    let y = null;

    if (typeof target === "number" && !Number.isNaN(target)) {
      y = target;
    } else if (typeof target === "string") {
      const el = document.querySelector(target);
      if (el) {
        y = el.getBoundingClientRect().top + window.scrollY + offset;
      }
    } else if (target && target.nodeType === 1) {
      y = target.getBoundingClientRect().top + window.scrollY + offset;
    }

    if (y === null) return;

    y = Math.max(0, y);

    if (immediate) {
      window.scrollTo({ top: y, left: 0, behavior: "instant" });
    } else if (
      typeof options.duration === "number" &&
      typeof window.gsap !== "undefined"
    ) {
      window.gsap.to(window, {
        duration: options.duration,
        scrollTo: { y, autoKill: true },
        ease: "power2.inOut",
        overwrite: "auto",
      });
    } else {
      window.scrollTo({ top: y, left: 0, behavior: "smooth" });
    }

    this.scheduleRefreshDimensions();
  }

  scrollTo(target, options = {}) {
    this.scrollToNative(target, options);
  }

  scrollToTop() {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    this.scheduleRefreshDimensions();
  }

  scrollToElement(selector, options = {}) {
    const element = document.querySelector(selector);
    if (!element) return;
    this.scrollToNative(element, {
      duration: 1.5,
      offset: this._getAnchorOffset(),
      ...options,
    });
  }

  pause() {}

  resume() {}

  destroy() {
    if (this._layoutRefreshTimer !== null) {
      window.clearTimeout(this._layoutRefreshTimer);
      this._layoutRefreshTimer = null;
    }
    if (this._onScrollActivity) {
      window.removeEventListener("scroll", this._onScrollActivity);
      this._onScrollActivity = null;
    }
    if (this._onTouchStart) {
      window.removeEventListener("touchstart", this._onTouchStart);
      this._onTouchStart = null;
    }
    if (this._onTouchEnd) {
      window.removeEventListener("touchend", this._onTouchEnd);
      window.removeEventListener("touchcancel", this._onTouchEnd);
      this._onTouchEnd = null;
    }
    if (this._onHashChange) {
      window.removeEventListener("hashchange", this._onHashChange);
      this._onHashChange = null;
    }
    if (this._onHashClick) {
      document.removeEventListener("click", this._onHashClick);
      this._onHashClick = null;
    }
    if (this._releaseHashFollow) {
      window.removeEventListener("wheel", this._releaseHashFollow);
      window.removeEventListener("touchmove", this._releaseHashFollow);
      this._releaseHashFollow = null;
    }
    this._postMountImgListenersBound = false;
    this.isInitialized = false;
  }

  getScrollProgress() {
    const range =
      document.documentElement.scrollHeight - window.innerHeight;
    return range <= 0 ? 1 : window.scrollY / range;
  }

  getScrollPosition() {
    return typeof window.scrollY === "number"
      ? window.scrollY
      : window.pageYOffset;
  }

  getScrollDirection() {
    return 1;
  }

  setupScrollTriggerProxy() {}
}

export default SmoothScroll;
